import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Path } from '../Routing/Paths';

const Page = styled.main`
  display: flex;
  min-height: 100dvh;
  width: 100%;
  overflow-x: hidden;
  flex-direction: column;
  background: #ffffff;
  color: #1b2457;
`;

const CurvedHeader = styled.header`
  position: relative;
  display: flex;
  min-height: clamp(340px, 48dvh, 440px);
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px 72px;
  background: #f5f6f8;
  text-align: center;
  clip-path: circle(
    clamp(450px, 110vw, 600px) at 50% calc(100% - clamp(450px, 110vw, 600px))
  );

  @media (max-width: 390px) {
    min-height: 300px;
    padding: 32px 20px 52px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    min-height: 270px;
    padding: 24px 20px 44px;
  }
`;

const WarningImage = styled.img`
  display: block;
  width: clamp(112px, 32vw, 172px);
  height: auto;

  @media (max-width: 390px) {
    width: 96px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    width: 76px;
  }
`;

const Title = styled.h1`
  max-width: 440px;
  margin: 28px 0 0;
  font-family: 'Fira Sans', sans-serif;
  font-size: clamp(30px, 5vw, 40px);
  font-weight: 700;
  line-height: 110%;
  letter-spacing: 0;
  text-align: center;
  overflow-wrap: anywhere;

  @media (max-width: 390px) {
    margin-top: 18px;
    font-size: 26px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    margin-top: 14px;
    font-size: 24px;
  }
`;

const Content = styled.section`
  display: flex;
  width: 100%;
  max-width: 680px;
  box-sizing: border-box;
  flex: 1;
  flex-direction: column;
  margin: 0 auto;
  padding: clamp(28px, 5dvh, 48px) clamp(24px, 6vw, 48px)
    max(32px, env(safe-area-inset-bottom));

  @media (max-width: 390px) {
    padding: 22px 20px max(20px, env(safe-area-inset-bottom));
  }

  @media (max-height: 600px) and (orientation: landscape) {
    padding-block: 18px max(18px, env(safe-area-inset-bottom));
  }
`;

const Instructions = styled.div`
  font-family: 'Fira Sans', sans-serif;
  font-size: clamp(16px, 2.6vw, 19px);
  font-weight: 400;
  line-height: 145%;
  letter-spacing: 0;
  overflow-wrap: anywhere;
  margin-bottom: 60px;

  & > p {
    margin: 0 0 20px;
  }

  & > ul {
    margin: 0;
    padding-left: 24px;
  }

  & li:not(:last-child) {
    margin-bottom: 10px;
  }

  @media (max-width: 390px) {
    font-size: 15px;
    line-height: 135%;

    & > p {
      margin-bottom: 14px;
    }

    & li:not(:last-child) {
      margin-bottom: 7px;
    }
  }
`;

const CopyButton = styled.button`
  width: 100%;
  height: 56px;
  margin-top: 120px;
  border: 0;
  border-radius: 5px;
  background: #52ae67;
  color: #ffffff;
  font-family: 'Fira Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;

  @media (max-width: 390px) {
    height: 48px;
    margin-top: 22px;
    font-size: 17px;
  }
`;

const warningImage = require('../Assets/KindredPluginActivation/warningCircle.png');

const KindredPluginActivationAlternateBrowser: React.FC = () => {
  const { t } = useTranslation();

  const copyUrl = async () => {
    await navigator.clipboard.writeText(
      `${window.location.origin}${Path.KindredPluginActivationHome}`
    );
  };

  return (
    <Page>
      <CurvedHeader>
        <WarningImage
          src={warningImage}
          alt={t('KindredPluginActivation.alternateBrowser.warningAlt')}
        />
        <Title>
          <Trans
            i18nKey="KindredPluginActivation.alternateBrowser.title"
            t={t}
            components={{ br: <br /> }}
          />
        </Title>
      </CurvedHeader>
      <Content>
        <Instructions>
          <p>{t('KindredPluginActivation.alternateBrowser.intro')}</p>
          <ul>
            <li>{t('KindredPluginActivation.alternateBrowser.copyStep')}</li>
            <li>{t('KindredPluginActivation.alternateBrowser.openStep')}</li>
            <li>{t('KindredPluginActivation.alternateBrowser.pasteStep')}</li>
          </ul>
        </Instructions>
        <CopyButton type="button" onClick={copyUrl}>
          {t('KindredPluginActivation.alternateBrowser.copyUrl')}
        </CopyButton>
      </Content>
    </Page>
  );
};

export default KindredPluginActivationAlternateBrowser;
